import PropTypes from 'prop-types';
import __ from "ramda/es/__";
import compose from "ramda/es/compose";
import curry from "ramda/es/curry";
import head from "ramda/es/head";
import map from "ramda/es/map";
import values from "ramda/es/values";
import cx from "ramda-extension/es/cx";
import isNilOrEmpty from "ramda-extension/es/isNilOrEmpty";
import React, { Component } from 'react';
import { withDefaultComponentProps } from '../../../decorators/withDefaultComponentProps';
import { classNamesForModifierOverDisplays } from '../../../utils/cssUtils';
import { displaySizes } from '../constants';
import { displaySize, displaySizeAlignType, displaySizeGridColPosition, pushesDisplaySize } from '../gridPropTypesValidators';
import { classNamesForModifierAndDisplay } from '../gridUtils';
const COMPONENT_NAME = 'GridCol';
class GridCol extends Component {
    static CSS_MODIFIER_PREFIX = 'Grid-col--';
    static CSS_MODIFIER_PUSH = 'Push';
    static CSS_MODIFIER_INVERSE_PUSH = 'InversePush';
    static CSS_MODIFIER_NOT_GROW = 'NotGrow';
    getAlignments = ()=>{
        if (!isNilOrEmpty(this.context.alignmentsForDisplays) && isNilOrEmpty(this.props.alignmentsForDisplays)) {
            return this.context.alignmentsForDisplays;
        }
        return this.props.alignmentsForDisplays;
    };
    classNamesForModifierOverDisplaysWithPrefix = curry(classNamesForModifierOverDisplays)('Grid-col--');
    classNamesForModifierAndDisplayWithoutPrefixAndSuffix = compose(map(classNamesForModifierAndDisplay(__, '', '', this.classNamesForModifierOverDisplaysWithPrefix)));
    render() {
        const { alignmentsForDisplays, children, className, positionsForDisplays, pushesForDisplays, inversePushesForDisplays, notGrowForDisplays, sizeALL, sizeXS, sizeSM, sizeMD, sizeLG, sizeXL, ...other } = this.props;
        const alignmentsClassNames = this.classNamesForModifierAndDisplayWithoutPrefixAndSuffix(this.getAlignments());
        const sizesForDisplays = [
            {
                [displaySizes.XS]: sizeXS
            },
            {
                [displaySizes.SM]: sizeSM
            },
            {
                [displaySizes.MD]: sizeMD
            },
            {
                [displaySizes.LG]: sizeLG
            },
            {
                [displaySizes.XL]: sizeXL
            },
            {
                [displaySizes.ALL]: sizeALL
            }
        ].filter((item)=>!!compose(head, values)(item));
        const columnSizeClassNames = this.classNamesForModifierAndDisplayWithoutPrefixAndSuffix(sizesForDisplays);
        const pushesClassNames = compose(map(classNamesForModifierAndDisplay(__, GridCol.CSS_MODIFIER_PUSH, '', this.classNamesForModifierOverDisplaysWithPrefix)))(pushesForDisplays);
        const inversePushesClassNames = compose(map(classNamesForModifierAndDisplay(__, GridCol.CSS_MODIFIER_INVERSE_PUSH, '', this.classNamesForModifierOverDisplaysWithPrefix)))(inversePushesForDisplays);
        const positionsClassName = this.classNamesForModifierAndDisplayWithoutPrefixAndSuffix(positionsForDisplays);
        const notGrowClassNames = this.classNamesForModifierOverDisplaysWithPrefix(GridCol.CSS_MODIFIER_NOT_GROW, notGrowForDisplays);
        const finalModifiersClassNames = [].concat(alignmentsClassNames, columnSizeClassNames, pushesClassNames, inversePushesClassNames, positionsClassName, notGrowClassNames).join(' ');
        return /*#__PURE__*/ React.createElement("div", {
            className: cx('Grid-col', finalModifiersClassNames, className),
            ...other
        }, children);
    }
}
GridCol.displayName = 'GridCol';
GridCol.propTypes = {
    /**
	 * Which alignment (constants.alignTypes) will be set for display (constants.displaySizes). It is possible to set several pairs displaySize:alignTypes.
	 */ alignmentsForDisplays: PropTypes.arrayOf(displaySizeAlignType),
    /**
	 * Children will be rendered within the `GridCol`.
	 */ children: PropTypes.node,
    /**
	 * Custom `className` for the root.
	 */ className: PropTypes.string,
    /**
	 * How many columns the column moves other columns (constants.displaySizes). It is possible to set several pairs displaySize:{push number}.
	 */ inversePushesForDisplays: PropTypes.arrayOf(pushesDisplaySize),
    /**
	 * Column with this sign will not grow. {display}NotGrow CSS selector will be set to display(s) (constants.displaySizes) pass as values in array.
	 */ notGrowForDisplays: PropTypes.arrayOf(displaySize),
    /**
	 * Which position (GridColPosition type) will be set for display (constants.displaySizes). It is possible to set several pairs displaySize:GridColPosition.
	 */ positionsForDisplays: PropTypes.arrayOf(displaySizeGridColPosition),
    /**
	 * How many columns this column pushes will be set for display (constants.displaySizes). It is possible to set several pairs displaySize:{push number}.
	 */ pushesForDisplays: PropTypes.arrayOf(pushesDisplaySize),
    /**
	 * Creates CSS selector Grid-col--xs{value} where value is this property.
	 * It's using xs modifier to correctly reflect desired grid layout
	 */ sizeALL: PropTypes.number,
    /**
	 * Creates CSS selector Grid-col--lg{value} where value is this property.
	 */ sizeLG: PropTypes.number,
    /**
	 * Creates CSS selector Grid-col--md{value} where value is this property.
	 */ sizeMD: PropTypes.number,
    /**
	 * Creates CSS selector Grid-col--sm{value} where value is this property.
	 */ sizeSM: PropTypes.number,
    /**
	 * Creates CSS selector Grid-col--xl{value} where value is this property.
	 */ sizeXL: PropTypes.number,
    /**
	 * Creates CSS selector Grid-col--xs{value} where value is this property.
	 */ sizeXS: PropTypes.number
};
GridCol.contextTypes = {
    alignmentsForDisplays: PropTypes.arrayOf(displaySizeAlignType)
};
export { GridCol };
export default withDefaultComponentProps(COMPONENT_NAME, {
    alignmentsForDisplays: [],
    positionsForDisplays: [],
    pushesForDisplays: [],
    inversePushesForDisplays: []
}, GridCol);
